import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import api from "../services/api";
import { useQueryClient } from "@tanstack/react-query";

const ExportContext = createContext(null);

export const useExport = () => {
  const ctx = useContext(ExportContext);
  if (!ctx) throw new Error("useExport must be used within ExportProvider");
  return ctx;
};

const INITIAL_STATE = {
  isRunning: false,
  isDone: false,
  isError: false,
  progress: 0,
  message: "",
  errorMessage: "",
  // ready-to-download data
  xmlContent: null,
  filename: null,
  exportLabel: "", // e.g. "Agnum (42 docs)"
  warningMessage: "",
  missingSupplierInfos: [],
};

export const ExportProvider = ({ children }) => {
  const [exportState, setExportState] = useState(INITIAL_STATE);
  const queryClient = useQueryClient();

  // Prevent double-start
  const isRunningRef = useRef(false);
  // Allow cancellation of the polling loop
  const cancelRef = useRef(false);

  const dismissDone = useCallback(() => {
    setExportState(INITIAL_STATE);
  }, []);

  /**
   * Start an XML export in the background.
   * params: { exportType, document_ids, use_matches, pozymiai, include_pvm_in_total,
   *           spacy_model_size, override_pvm_code, recalculate_vat, matcher_type,
   *           is_business_trip, business_trip_document_number, exportLabel }
   */
  const startExport = useCallback(
    async (params) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;
      cancelRef.current = false;

      const {
        exportType,
        document_ids,
        use_matches,
        pozymiai,
        include_pvm_in_total,
        spacy_model_size,
        override_pvm_code,
        recalculate_vat,
        matcher_type,
        is_business_trip,
        business_trip_document_number,
        exportLabel = "",
      } = params;

      setExportState({
        ...INITIAL_STATE,
        isRunning: true,
        progress: 0,
        message: "Starting export...",
        exportLabel,
      });

      try {
        const endpoint =
          exportType === "agnum_summary" ? "/api/export_summary_xml/" : "/api/export_xml/";

        const response = await api.post(endpoint, {
          export_type: exportType,
          document_ids,
          use_matches,
          pozymiai: pozymiai || [],
          include_pvm_in_total: include_pvm_in_total !== undefined ? include_pvm_in_total : true,
          spacy_model_size: spacy_model_size || "lg",
          override_pvm_code: override_pvm_code || "",
          recalculate_vat: !!recalculate_vat,
          is_business_trip: !!is_business_trip,
          business_trip_document_number: business_trip_document_number || "",
          matcher_type: matcher_type || "llm",
          async: true,
        });

        let result;

        if (response.data.async && response.data.task_id) {
          // Poll for task completion
          result = await pollTask(response.data.task_id, (progress) => {
            if (!cancelRef.current) {
              setExportState((prev) => ({
                ...prev,
                progress: progress.progress || 0,
                message: progress.message || "Processing...",
              }));
            }
          });
        } else {
          // Sync response
          result = response.data;
        }

        if (cancelRef.current) {
          isRunningRef.current = false;
          setExportState(INITIAL_STATE);
          return;
        }

        if (result.xml_content && result.filename) {
          queryClient.invalidateQueries({ queryKey: ["documents"] });
          isRunningRef.current = false;
          setExportState({
            ...INITIAL_STATE,
            isDone: true,
            progress: 100,
            message: "Export complete",
            xmlContent: result.xml_content,
            filename: result.filename,
            exportLabel,
            warningMessage: result.warning_message || "",
            missingSupplierInfos: Array.isArray(result.missing_supplier_infos)
              ? result.missing_supplier_infos
              : [],
          });
        } else {
          throw new Error("Invalid export result: missing xml_content or filename");
        }
      } catch (err) {
        isRunningRef.current = false;
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Export failed";
        setExportState({
          ...INITIAL_STATE,
          isDone: true,
          isError: true,
          message: "",
          errorMessage: msg,
          exportLabel,
        });
      }
    },
    [queryClient]
  );

  return (
    <ExportContext.Provider value={{ exportState, startExport, dismissDone }}>
      {children}
    </ExportContext.Provider>
  );
};

ExportProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ExportContext;

/* ── Polling helper (module-level, no hooks) ── */
async function pollTask(taskId, onProgress) {
  const maxAttempts = 360; // 30 min at 5s interval
  let attempts = 0;

  while (attempts < maxAttempts) {
    const response = await api.get(`/api/export_task_status/${taskId}/`);
    const { status, progress, message, result, error } = response.data;

    if (onProgress) onProgress({ status, progress, message });

    if (status === "completed") return result;
    if (status === "failed") throw new Error(error || "Export failed");

    await new Promise((res) => setTimeout(res, 5000));
    attempts++;
  }

  throw new Error("Export timed out");
}
